import React from 'react';
import DataTable, { TableProps, TableStyles } from 'react-data-table-component';
import moment from 'moment';
import { FolderOpen } from 'lucide-react';

const paginationComponentOptions = {
    rowsPerPageText: 'Linhas por página:',
    rangeSeparatorText: 'de',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Todos',
};

interface DataTableProps<T> extends TableProps<T> {
    loading?: boolean;
    data: Array<T & any>;
}

const LoadingState = () => (
    <div className="sf-table-shell px-4 py-5">
        <div className="space-y-3">
            {[0, 1, 2, 3, 4].map((item) => (
                <div key={item} className="grid grid-cols-[1.4fr_1fr_0.8fr] gap-3">
                    <div className="sf-skeleton h-11" />
                    <div className="sf-skeleton h-11" />
                    <div className="sf-skeleton h-11" />
                </div>
            ))}
        </div>
    </div>
);

const EmptyState = () => (
    <div className="sf-empty-state rounded-[1.45rem] px-5 py-10 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-base-100/60 text-base-content/55">
            <FolderOpen className="text-lg" aria-hidden="true" />
        </div>
        <div className="mt-4 text-sm font-semibold text-base-content">Nenhum registro</div>
        <div className="mt-1 text-sm text-base-content/60">Ajuste os filtros ou aguarde novos dados.</div>
    </div>
);

const CustomDataTable = <T extends any>({
    loading,
    data,
    customStyles: customStylesProp,
    ...props
}: DataTableProps<T>) => {
    const dataIsEmpty = !data || data.length === 0;

    if (loading) {
        return <LoadingState />;
    }

    if (dataIsEmpty) {
        return <EmptyState />;
    }

    const currentTime = moment();
    const baseStyles: TableStyles = {
        table: {
            style: {
                backgroundColor: 'transparent',
                color: 'oklch(var(--bc))',
            },
        },
        tableWrapper: {
            style: {
                display: 'block',
            },
        },
        headRow: {
            style: {
                minHeight: '3.3rem',
                borderBottomColor: 'oklch(var(--bc) / 0.08)',
                backgroundColor: 'transparent',
            },
        },
        headCells: {
            style: {
                paddingLeft: '1.15rem',
                paddingRight: '1.15rem',
                color: 'oklch(var(--bc) / 0.56)',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.16em',
                fontSize: '0.68rem',
                fontWeight: 700,
            },
        },
        rows: {
            style: {
                minHeight: '4.2rem',
                color: 'oklch(var(--bc))',
                borderBottomColor: 'oklch(var(--bc) / 0.06)',
                backgroundColor: 'transparent',
            },
            highlightOnHoverStyle: {
                backgroundColor: 'rgb(var(--sf-accent-main-rgb) / 0.05)',
                color: 'oklch(var(--bc))',
                borderBottomColor: 'oklch(var(--bc) / 0.06)',
                outline: 'none',
                outlineStyle: 'none' as const,
                outlineWidth: 0,
                outlineColor: 'transparent',
                transitionDuration: '0.16s',
                transitionProperty: 'background-color, transform',
            },
            stripedStyle: {
                backgroundColor: 'rgb(var(--sf-accent-main-rgb) / 0.025)',
            },
        },
        cells: {
            style: {
                paddingLeft: '1.15rem',
                paddingRight: '1.15rem',
                color: 'oklch(var(--bc))',
            },
        },
        pagination: {
            style: {
                minHeight: '4rem',
                backgroundColor: 'transparent',
                color: 'oklch(var(--bc) / 0.68)',
                borderTopColor: 'oklch(var(--bc) / 0.08)',
            },
            pageButtonsStyle: {
                borderRadius: '0.85rem',
                color: 'oklch(var(--bc) / 0.65)',
                fill: 'oklch(var(--bc) / 0.65)',
                '&:disabled': {
                    color: 'oklch(var(--bc) / 0.3)',
                    fill: 'oklch(var(--bc) / 0.3)',
                },
                '&:hover:not(:disabled)': {
                    color: 'oklch(var(--bc))',
                    fill: 'oklch(var(--bc))',
                    backgroundColor: 'rgb(var(--sf-accent-main-rgb) / 0.08)',
                },
            },
        },
        noData: {
            style: {
                color: 'oklch(var(--bc) / 0.6)',
                backgroundColor: 'transparent',
            },
        },
    };
    const customStyles: TableStyles = customStylesProp ? { ...baseStyles, ...customStylesProp } : baseStyles;

    const conditionalRowStyles = [
        {
            when: (row: any) => {
                const createdAt = row.createdAt ? moment(row.createdAt) : null;
                const updatedAt = row.updatedAt ? moment(row.updatedAt) : null;
                const minTime = 5;

                const createdAtDifference = createdAt ? currentTime.diff(createdAt, 'minutes') : Infinity;
                const updatedAtDifference = updatedAt ? currentTime.diff(updatedAt, 'minutes') : Infinity;

                return createdAtDifference < minTime || updatedAtDifference < minTime;
            },
            style: {
                backgroundColor: 'oklch(var(--su) / 0.1)',
            },
        },
    ];

    return (
        <div className="sf-table-shell">
            <DataTable
                pagination
                paginationRowsPerPageOptions={[5, 15, 20]}
                paginationComponentOptions={paginationComponentOptions}
                conditionalRowStyles={conditionalRowStyles}
                customStyles={customStyles}
                data={data}
                expandableRows={!!props.expandableRowsComponent}
                expandableRowsComponent={props.expandableRowsComponent}
                {...props}
            />
        </div>
    );
};

export default CustomDataTable;
